import { useMutation, useQuery } from "@tanstack/react-query";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { unwrap } from "@wilco/api";
import { Button, Notice, TextField } from "@wilco/ui";
import { useState } from "react";
import { api } from "../auth.ts";
import { formatDate, formatMoney, formatNumber, t } from "../i18n.ts";
import { productInfo, productInfos } from "../lookups.ts";
import { ProductSearch } from "../stock/RequestDraftPage.tsx";
import { errorNotice } from "../stock/ShelfPage.tsx";
import { parseQty } from "../stock/qty.ts";
import { costingApi, formatMonth, monthOf, type BomLine } from "./api.ts";
import { CostingHead } from "./CostingNav.tsx";

const route = getRouteApi("/app/costing/prices");

const signed = (n: number) => `${n > 0 ? "+" : ""}${formatMoney(n, "UAH")}`;

function useNames(ids: string[]) {
  const q = useQuery({ queryKey: ["lookups", "products", ...ids], queryFn: () => productInfos(ids), enabled: ids.length > 0 });
  return (id: string) => {
    const p = q.data?.get(id);
    return p ? `${p.sku} ${p.name}` : id;
  };
}

/**
 * «Динаміка цін» (СВ-16...СВ-19): ряд цен приёмок компонента без ПДВ в
 * гривне по фактическому курсу, себестоимость серийника с разбивкой и
 * трендом версии, спецификация по последним ценам против последнего
 * серийника, вклад компонентов между месяцами и черновик замены, который
 * рабочую спецификацию не меняет.
 */
export function PricesPage() {
  const search = route.useSearch();
  const navigate = useNavigate();
  const set = (next: { product?: string; serial?: string }) => void navigate({ to: "/costing/prices", search: { ...search, ...next }, replace: true });

  return (
    <div className="page costing">
      <CostingHead title={t("costing.title")} />
      <div className="costing-grid">
        <ComponentPrices product={search.product} onProduct={(product) => set({ product })} />
        <LotSection serial={search.serial} onSerial={(serial) => set({ serial })} />
      </div>
    </div>
  );
}

function ComponentPrices({ product, onProduct }: { product?: string; onProduct: (id: string) => void }) {
  const series = useQuery({ queryKey: ["costing", "component", product], queryFn: () => costingApi.componentPrices(product ?? ""), enabled: Boolean(product) });
  const info = useQuery({ queryKey: ["lookups", "product", product], queryFn: () => productInfo(product ?? ""), enabled: Boolean(product) });
  return (
    <section className="stock-section" aria-labelledby="prices-component">
      <h2 id="prices-component" className="desk-group-title">
        {t("prices.component")}
      </h2>
      <ProductSearch onPick={(p) => onProduct(p.id)} />
      {info.data ? (
        <p className="prices-picked">
          <span className="w-num">{info.data.sku}</span> {info.data.name}
        </p>
      ) : null}
      {series.error ? <Notice tone="danger" title={errorNotice(series.error).title} /> : null}
      {series.data && series.data.length === 0 ? <p className="w-muted">{t("prices.noReceipts")}</p> : null}
      {series.data?.length ? (
        <ul className="payments">
          {series.data.map((p, i) => (
            <li key={`${p.receivedOn}-${String(i)}`} className="payment">
              <span className="w-num">{formatDate(p.receivedOn)}</span>
              <span className="payment-ref w-num" data-tone={p.change == null ? undefined : p.change > 0 ? "up" : "down"}>
                {p.change == null ? "" : signed(p.change)}
              </span>
              <span className="w-num payment-sum">{formatMoney(p.unitCost, "UAH")}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

/** Изделие серийника: вариант и версия из справочника, по ним тренд и спецификация. */
async function lotProduct(serial: string): Promise<{ id: string; version: string | null }> {
  const card = await unwrap(api.GET("/api/v1/service/cards/scan", { params: { query: { code: serial } } }));
  const product = await unwrap(api.GET("/api/v1/catalog/products/{id}", { params: { path: { id: card.productId } } }));
  return { id: card.productId, version: product.version ?? null };
}

function LotSection({ serial, onSerial }: { serial?: string; onSerial: (s: string) => void }) {
  const [typed, setTyped] = useState(serial ?? "");
  const lot = useQuery({ queryKey: ["costing", "lot", serial], queryFn: () => costingApi.lot(serial ?? ""), enabled: Boolean(serial), retry: false });
  const product = useQuery({ queryKey: ["costing", "lotProduct", serial], queryFn: () => lotProduct(serial ?? ""), enabled: Boolean(serial), retry: false });
  const from = monthOf(-11);
  const to = monthOf(0);
  const trend = useQuery({
    queryKey: ["costing", "trend", product.data?.id, product.data?.version, from, to],
    queryFn: () => costingApi.trend(product.data?.id ?? "", product.data?.version ?? null, from, to),
    enabled: Boolean(product.data),
  });
  const l = lot.data;
  const names = useNames(l ? [...Object.keys(l.components), ...Object.keys(l.packing)] : []);

  return (
    <section className="stock-section" aria-labelledby="prices-lot">
      <h2 id="prices-lot" className="desk-group-title">
        {t("prices.lot")}
      </h2>
      <form
        className="qty-row"
        onSubmit={(e) => {
          e.preventDefault();
          if (typed.trim()) onSerial(typed.trim());
        }}
      >
        <TextField label={t("prices.serial")} hideLabel placeholder={t("prices.serial")} variant="scan" value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" />
        <Button type="submit" variant="primary">
          {t("prices.show")}
        </Button>
      </form>
      {lot.error ? <Notice tone="danger" title={errorNotice(lot.error).title} /> : null}
      {l ? (
        <dl className="cost-sum">
          {Object.entries(l.components).map(([id, amount]) => (
            <div key={`c-${id}`}>
              <dt>{names(id)}</dt>
              <dd className="w-num">{formatMoney(amount, "UAH")}</dd>
            </div>
          ))}
          {Object.entries(l.packing).map(([id, amount]) => (
            <div key={`p-${id}`}>
              <dt>{`${t("prices.packing")}: ${names(id)}`}</dt>
              <dd className="w-num">{formatMoney(amount, "UAH")}</dd>
            </div>
          ))}
          <div>
            <dt>{t("prices.fot")}</dt>
            <dd className="w-num">{formatMoney(l.fot, "UAH")}</dd>
          </div>
          <div data-total="">
            <dt>{t("prices.total")}</dt>
            <dd className="w-num">{formatMoney(l.total, "UAH")}</dd>
          </div>
        </dl>
      ) : null}
      {trend.data?.length ? (
        <>
          <h3 className="prices-sub">{t("prices.trend")}</h3>
          <ul className="prices-trend">
            {trend.data.map((p) => (
              <li key={p.month}>
                <span>{formatMonth(p.month)}</span>
                <span className="w-num">{formatMoney(p.cost, "UAH")}</span>
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {product.data && serial ? <BomToday product={product.data.id} version={product.data.version} lastLot={serial} /> : null}
      {product.data ? <ContributionView product={product.data.id} version={product.data.version} /> : null}
    </section>
  );
}

interface DraftRow {
  product: string;
  qty: string;
}

/** Спецификация по последним ценам против последнего серийника и прикидка замены (СВ-19). */
function BomToday({ product, version, lastLot }: { product: string; version: string | null; lastLot: string }) {
  const bom = useQuery({ queryKey: ["costing", "bom", product, version, lastLot], queryFn: () => costingApi.bom(product, version, lastLot) });
  const [draft, setDraft] = useState<DraftRow[] | null>(null);
  const [replacing, setReplacing] = useState<number | null>(null);
  const cost = useMutation({ mutationFn: (rows: DraftRow[]) => costingApi.draftCost(rows.map((r) => ({ product: r.product, qty: parseQty(r.qty) ?? 0 }))) });
  const lines: BomLine[] = bom.data ?? [];
  const names = useNames([...lines.map((l) => l.product), ...(draft?.map((d) => d.product) ?? [])]);
  const today = lines.reduce((a, l) => a + l.today, 0);
  const last = lines.reduce((a, l) => a + l.lastLot, 0);

  return (
    <>
      <h3 className="prices-sub">{t("prices.bom", { serial: lastLot })}</h3>
      {bom.error ? <Notice tone="danger" title={errorNotice(bom.error).title} /> : null}
      {lines.length > 0 ? (
        <div className="costing-table" role="table" aria-label={t("prices.bomLabel")} data-cols="4">
          <div role="row" data-head="">
            <span role="columnheader">{t("prices.component")}</span>
            <span role="columnheader" className="num">
              {t("prices.today")}
            </span>
            <span role="columnheader" className="num">
              {t("prices.lastLot")}
            </span>
            <span role="columnheader" className="num">
              {t("prices.difference")}
            </span>
          </div>
          {lines.map((l) => (
            <div role="row" key={l.product}>
              <span role="cell">{`${names(l.product)} × ${formatNumber(l.qty)}`}</span>
              <span role="cell" className="num w-num">
                {formatMoney(l.today, "UAH")}
              </span>
              <span role="cell" className="num w-num">
                {formatMoney(l.lastLot, "UAH")}
              </span>
              <span role="cell" className="num w-num">
                {signed(l.difference)}
              </span>
            </div>
          ))}
          <div role="row" data-total="">
            <span role="cell">{t("prices.total")}</span>
            <span role="cell" className="num w-num">
              {formatMoney(today, "UAH")}
            </span>
            <span role="cell" className="num w-num">
              {formatMoney(last, "UAH")}
            </span>
            <span role="cell" className="num w-num">
              {signed(today - last)}
            </span>
          </div>
        </div>
      ) : null}
      {lines.length > 0 && !draft ? (
        <Button variant="secondary" onClick={() => setDraft(lines.map((l) => ({ product: l.product, qty: String(l.qty) })))}>
          {t("prices.draft")}
        </Button>
      ) : null}
      {draft ? (
        <form
          className="stock-form prices-draft"
          onSubmit={(e) => {
            e.preventDefault();
            cost.mutate(draft);
          }}
        >
          <p className="w-muted">{t("prices.draftHint")}</p>
          {draft.map((d, i) => (
            <div key={`${String(i)}-${d.product}`} className="prices-draft-row">
              <span>{names(d.product)}</span>
              <TextField label={t("sales.qty")} hideLabel inputMode="decimal" value={d.qty} onChange={(e) => setDraft(draft.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)))} />
              <Button variant="ghost" type="button" onClick={() => setReplacing(i)}>
                {t("prices.replace")}
              </Button>
            </div>
          ))}
          {replacing !== null ? (
            <ProductSearch
              onPick={(p) => {
                setDraft(draft.map((x, j) => (j === replacing ? { ...x, product: p.id } : x)));
                setReplacing(null);
              }}
            />
          ) : null}
          <Button type="submit" variant="primary" disabled={cost.isPending}>
            {t("prices.calc")}
          </Button>
          {cost.error ? <Notice tone="danger" title={errorNotice(cost.error).title} /> : null}
          {cost.data ? <Notice tone="info" title={t("prices.draftTotal", { sum: formatMoney(cost.data.total, "UAH"), diff: signed(cost.data.total - today) })} /> : null}
        </form>
      ) : null}
    </>
  );
}

/** Вклад компонентов в изменение себестоимости между двумя месяцами (СВ-18). */
function ContributionView({ product, version }: { product: string; version: string | null }) {
  const after = monthOf(-1);
  const before = monthOf(-2);
  const list = useQuery({ queryKey: ["costing", "contribution", product, version, before, after], queryFn: () => costingApi.contribution(product, version, before, after) });
  const names = useNames(list.data?.map((c) => c.product) ?? []);
  return (
    <>
      <h3 className="prices-sub">{t("prices.contribution", { before: formatMonth(before), after: formatMonth(after) })}</h3>
      {list.error ? <Notice tone="danger" title={errorNotice(list.error).title} /> : null}
      {list.data && list.data.length === 0 ? <p className="w-muted">{t("prices.noContribution")}</p> : null}
      <ul className="payments">
        {list.data?.map((c) => (
          <li key={c.product} className="payment">
            <span>{names(c.product)}</span>
            <span className="payment-ref w-muted w-num">{`${formatNumber(c.percent)} %`}</span>
            <span className="w-num payment-sum">{signed(c.amount)}</span>
          </li>
        ))}
      </ul>
    </>
  );
}
